import React, { useState } from "react";
import { Link } from "react-router-dom";

/**
 * Page publique de prévisualisation des styles d'avatar pour les 10 agents GABRYX.
 * Accès : /preview-avatars
 *
 * L'utilisateur choisit un style, puis on l'appliquera à tous les agents sur la Landing.
 */

const AGENTS = [
  { code: "gaby", name: "Gaby", role: "CEO", color: "#3B82F6", bg: "1e3a8a" },
  { code: "commerce", name: "Jérôme", role: "VP Sales", color: "#D4A017", bg: "3d2f0f" },
  { code: "thierry", name: "Thierry", role: "Product Manager", color: "#F59E0B", bg: "78350f" },
  { code: "casper", name: "Casper", role: "Legal & Compliance", color: "#EF4444", bg: "7f1d1d" },
  { code: "clara", name: "Clara", role: "Marketing & Contenu", color: "#EC4899", bg: "831843" },
  { code: "ulrich", name: "Ulrich", role: "CFO", color: "#10B981", bg: "064e3b" },
  { code: "rhea", name: "Rhea", role: "RH", color: "#8B5CF6", bg: "4c1d95" },
  { code: "opera", name: "Opéra", role: "Ops & Support", color: "#06B6D4", bg: "164e63" },
  { code: "visio", name: "Visio", role: "Analytics", color: "#F97316", bg: "7c2d12" },
  { code: "voxy", name: "Voxy", role: "Voice AI", color: "#A855F7", bg: "581c87" },
];

const STYLES = [
  {
    id: "bottts-neutral",
    label: "🤖 Robots IA (actuel)",
    description: "Style DiceBear bottts — têtes de robots colorées",
    builder: (a) =>
      `https://api.dicebear.com/9.x/bottts-neutral/svg?seed=${a.code}&backgroundColor=${a.bg}&size=400`,
  },
  {
    id: "avataaars",
    label: "😀 Cartoon humain (Avataaars)",
    description: "Style Bitmoji / cartoon coloré populaire",
    builder: (a) =>
      `https://api.dicebear.com/9.x/avataaars/svg?seed=${a.code}&backgroundColor=${a.bg}&size=400`,
  },
  {
    id: "personas",
    label: "🎨 Personas (illustré moderne)",
    description: "Style Notion / Linear, illustration plate élégante",
    builder: (a) =>
      `https://api.dicebear.com/9.x/personas/svg?seed=${a.code}&backgroundColor=${a.bg}&size=400`,
  },
  {
    id: "notionists",
    label: "📝 Notionists (Notion)",
    description: "Le style officiel Notion, épuré et pro",
    builder: (a) =>
      `https://api.dicebear.com/9.x/notionists/svg?seed=${a.code}&backgroundColor=${a.bg}&size=400`,
  },
  {
    id: "lorelei",
    label: "✨ Lorelei (portrait élégant)",
    description: "Illustré, féminin/masculin doux, style éditorial",
    builder: (a) =>
      `https://api.dicebear.com/9.x/lorelei/svg?seed=${a.code}&backgroundColor=${a.bg}&size=400`,
  },
  {
    id: "micah",
    label: "🖍️ Micah (minimaliste)",
    description: "Illustration minimaliste, design contemporain",
    builder: (a) =>
      `https://api.dicebear.com/9.x/micah/svg?seed=${a.code}&backgroundColor=${a.bg}&size=400`,
  },
  {
    id: "adventurer",
    label: "🗺️ Adventurer (aventurier)",
    description: "Style dynamique et jeune, coloré",
    builder: (a) =>
      `https://api.dicebear.com/9.x/adventurer/svg?seed=${a.code}&backgroundColor=${a.bg}&size=400`,
  },
  {
    id: "open-peeps",
    label: "👥 Open Peeps (dessiné main)",
    description: "Style hand-drawn, chaleureux et humain",
    builder: (a) =>
      `https://api.dicebear.com/9.x/open-peeps/svg?seed=${a.code}&backgroundColor=${a.bg}&size=400`,
  },
  {
    id: "big-smile",
    label: "😄 Big Smile (cartoon joyeux)",
    description: "Style cartoon expressif, très friendly",
    builder: (a) =>
      `https://api.dicebear.com/9.x/big-smile/svg?seed=${a.code}&backgroundColor=${a.bg}&size=400`,
  },
  {
    id: "pixel-art",
    label: "🎮 Pixel Art (rétro gaming)",
    description: "Style 8-bit rétro, tech geek",
    builder: (a) =>
      `https://api.dicebear.com/9.x/pixel-art/svg?seed=${a.code}&backgroundColor=${a.bg}&size=400`,
  },
];

export default function AvatarPreview() {
  const [selected, setSelected] = useState("personas");

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="avatar-preview-page">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="preview-title">
              Aperçu des styles d'avatars — GABRYX
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Choisis le style qui correspond à ta vision. Chaque agent gardera son identité (couleur, rôle).
            </p>
          </div>
          <Link
            to="/"
            className="text-sm text-slate-400 hover:text-white transition"
            data-testid="back-to-landing"
          >
            ← Retour Landing
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 space-y-12">
        {STYLES.map((style) => (
          <section
            key={style.id}
            className={`rounded-3xl border transition-all p-6 ${
              selected === style.id
                ? "border-blue-500 bg-blue-500/5 shadow-2xl shadow-blue-500/10"
                : "border-slate-800 bg-slate-900/40"
            }`}
            data-testid={`style-section-${style.id}`}
          >
            <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
              <div>
                <h2 className="text-xl font-semibold" data-testid={`style-label-${style.id}`}>
                  {style.label}
                </h2>
                <p className="text-sm text-slate-400 mt-1">{style.description}</p>
              </div>
              <button
                onClick={() => setSelected(style.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                  selected === style.id
                    ? "bg-blue-500 text-white"
                    : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                }`}
                data-testid={`select-style-${style.id}`}
              >
                {selected === style.id ? "✓ Sélectionné" : "Choisir ce style"}
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {AGENTS.map((agent) => (
                <div
                  key={agent.code}
                  className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-slate-950/60 border border-slate-800/50"
                  data-testid={`agent-preview-${style.id}-${agent.code}`}
                >
                  <div
                    className="w-24 h-24 rounded-full overflow-hidden border-2"
                    style={{ borderColor: agent.color, backgroundColor: `#${agent.bg}` }}
                  >
                    <img
                      src={style.builder(agent)}
                      alt={agent.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-semibold">{agent.name}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                      {agent.role}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}

        {/* Sticky footer note */}
        <div className="rounded-2xl border border-blue-500/30 bg-blue-500/5 p-6 text-center">
          <p className="text-sm text-slate-300">
            💡 Dis-moi simplement le nom du style que tu préfères (ex : "je veux Personas" ou "Notionists c'est parfait")
            et je l'applique instantanément sur la Landing page + le dashboard.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Si aucun ne te plaît, on peut passer à des <strong>photos IA hyper-réalistes</strong> générées avec Nano Banana (Gemini).
          </p>
        </div>
      </div>
    </div>
  );
}
